import * as React from "react";
import {
  Alert,
  AutoComplete,
  Button,
  Card,
  Descriptions,
  Divider,
  Input,
  InputNumber,
  Mentions,
  message,
  Radio,
  Result,
  Select,
  Space,
  Spin,
  Steps,
  Table,
  Tag,
  Typography,
  Upload
} from "antd";
import type { ColumnsType } from "antd/lib/table";
import type { UploadFile } from "antd/lib/upload/interface";
import type { RcFile, UploadRequestOption as RcCustomRequestOptions } from "rc-upload/lib/interface";
import { CheckCircleOutlined, PrinterOutlined, UploadOutlined } from "@ant-design/icons";
import { ApiConfig } from "../utils/AuthorizedApiBase";
import {
  PrintClient,
  ApiException,
  GoodsReceiptAttachmentRefDto,
  GoodsReceiptIntakeContextDto,
  IncomingClaimOptionDto,
  IncomingOrderSearchResultDto,
  GoodsReceiptSubmitLineDto,
  GoodsReceiptSubmitRequestDto,
  GoodsReceiptSubmitResponseDto,
  GoodsReceiptsClient,
  MentionableAdmin,
  ReturnedShipmentResendResponseDto
} from "../utils/scanner-backend";
import { getApiErrorMessage } from "../utils/apiError";
import { formatGoodsReceiptNumber } from "../utils/documentNumbers";
import { useKeyboardScanner } from "../hooks/useKeyboardScanner";
import {
  ReturnedParcelDecision,
  ReturnedParcelLabelForm,
  ReturnedParcelResult
} from "./ReturnedParcelResend";

const { Text } = Typography;
const MIN_SEARCH_QUERY_LENGTH = 2;

const apiConfig = new ApiConfig();
const goodsReceiptsClient = new GoodsReceiptsClient(apiConfig);
const printClient = new PrintClient(new ApiConfig());

const generateSubmitIdempotencyKey = (orderCode: string): string => {
  const entropy = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

  return `goods-receipt-${orderCode}-${entropy}`;
};

type PrinterPort = "6101" | "6102";
const printerOptions: { label: string; value: PrinterPort }[] = [
  { label: "SKLAD", value: "6101" },
  { label: "HALA", value: "6102" }
];

type Step = 0 | 1 | 2;

type LineState = {
  key: string;
  productName: string;
  skuCode: string;
  receivedOrderProductId: number;
  skuId: number;
  batchNumber: string;
  batchExpirationDate: string | null;
  remainingQuantity: number;
  quantityAccepted: number;
  quantityQuarantine: number;
  quantityDamaged: number;
};

type CustomerReturnIntakeProps = {
  inCard?: boolean;
  /** False while another intake tab is shown; antd keeps a visited tab mounted, so its scans must not land here. */
  active?: boolean;
};

type SearchMode = "manual" | "auto";

// A parcel the courier brought back undelivered (S-507): the worker first decides whether it goes
// out again or its goods go back to stock.
type ReturnedParcelChoice = "decide" | "resend" | "restock";

type SuggestOption = {
  value: string;
  label: React.ReactNode;
  orderCode: string;
};

export default function CustomerReturnIntake({ inCard = false, active = true }: CustomerReturnIntakeProps) {
  const [step, setStep] = React.useState<Step>(0);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [hasSearched, setHasSearched] = React.useState(false);
  const [orders, setOrders] = React.useState<IncomingOrderSearchResultDto[]>([]);
  const latestSearchRequestId = React.useRef(0);
  // The scan that already opened its order: "Späť" or a repeated search must not reopen it.
  const autoOpenedScan = React.useRef<string | null>(null);
  const latestOrderContextRequestId = React.useRef(0);
  const searchDebounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const [selectedOrder, setSelectedOrder] = React.useState<IncomingOrderSearchResultDto | null>(
    null
  );
  const [claims, setClaims] = React.useState<IncomingClaimOptionDto[]>([]);
  const [selectedClaimId, setSelectedClaimId] = React.useState<number | null>(null);
  const [contextLoading, setContextLoading] = React.useState(false);
  const [intakeContext, setIntakeContext] =
    React.useState<GoodsReceiptIntakeContextDto | null>(null);
  const [lineStates, setLineStates] = React.useState<LineState[]>([]);
  const [returnedParcelChoice, setReturnedParcelChoice] = React.useState<ReturnedParcelChoice>("restock");
  const [resendResult, setResendResult] = React.useState<ReturnedShipmentResendResponseDto | null>(null);

  const [reasonText, setReasonText] = React.useState("");
  const [noteText, setNoteText] = React.useState("");
  const [mentionableAdmins, setMentionableAdmins] = React.useState<MentionableAdmin[]>([]);
  const [attachments, setAttachments] = React.useState<GoodsReceiptAttachmentRefDto[]>([]);
  const [uploadList, setUploadList] = React.useState<UploadFile[]>([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [printing, setPrinting] = React.useState(false);
  const [selectedPrinter, setSelectedPrinter] = React.useState<PrinterPort>("6101");
  const [submitResult, setSubmitResult] = React.useState<GoodsReceiptSubmitResponseDto | null>(
    null
  );
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [submitFinalizeInconsistency, setSubmitFinalizeInconsistency] = React.useState(false);
  const [submitIdempotencyKey, setSubmitIdempotencyKey] = React.useState<string | null>(null);

  React.useEffect(() => {
    void goodsReceiptsClient
      .mentionableAdmins()
      .then(setMentionableAdmins)
      .catch(error => console.error("Nepodarilo sa načítať kolegov pre označenia.", error));
  }, []);

  const loadOrderContext = React.useCallback(async (order: IncomingOrderSearchResultDto) => {
    const requestId = ++latestOrderContextRequestId.current;
    setContextLoading(true);
    try {
      const [loadedClaims, loadedContext] = await Promise.all([
        goodsReceiptsClient.getClaims(order.OrderCode),
        goodsReceiptsClient.getIntakeContext(order.OrderCode)
      ]);

      if (requestId !== latestOrderContextRequestId.current) {
        return;
      }

      setClaims(loadedClaims);
      setIntakeContext(loadedContext);
      setReturnedParcelChoice(loadedContext.ReturnedShipment ? "decide" : "restock");
      if (loadedClaims.length === 1) {
        setSelectedClaimId(loadedClaims[0].ClaimId);
        message.info(`K objednávke bola nájdená reklamácia ${loadedClaims[0].DisplayName} a bola automaticky vybraná.`);
      } else {
        setSelectedClaimId(null);
      }

      const initialLines: LineState[] = [];
      loadedContext.Lines.forEach(line => {
        line.AvailableBatchNumbers.forEach(batch => {
      initialLines.push({
            key: `${line.ReceivedOrderProductId}-${batch.SkuId}-${batch.BatchNumber ?? ""}`,
            productName: line.ProductName,
            skuCode: batch.SkuCode,
            receivedOrderProductId: line.ReceivedOrderProductId,
            skuId: batch.SkuId,
            batchNumber: batch.BatchNumber ?? "",
            batchExpirationDate: batch.BatchExpirationDate,
            remainingQuantity: batch.RemainingQuantity,
            quantityAccepted: 0,
            quantityQuarantine: 0,
            quantityDamaged: 0
          });
        });
      });
      setLineStates(initialLines);
      setStep(1);
    } catch (error) {
      if (requestId !== latestOrderContextRequestId.current) {
        return;
      }
      message.error(getApiErrorMessage(error));
    } finally {
      if (requestId === latestOrderContextRequestId.current) {
        setContextLoading(false);
      }
    }
  }, []);

  const handleSelectOrder = React.useCallback(
    async (order: IncomingOrderSearchResultDto) => {
      if (!order.IsSelectable) {
        message.error(order.BlockingReason || "Táto objednávka nie je vyberateľná.");
        return;
      }

      setSelectedOrder(order);
      setResendResult(null);
      setSubmitError(null);
      setSubmitFinalizeInconsistency(false);
      setSubmitResult(null);
      setSubmitIdempotencyKey(null);
      setAttachments([]);
      setUploadList([]);
      setReasonText("");
      setNoteText("");
      await loadOrderContext(order);
    },
    [loadOrderContext]
  );

  const performSearch = React.useCallback(async (query: string, mode: SearchMode = "manual") => {
    const trimmedQuery = query.trim();
    if (trimmedQuery.length > 0 && trimmedQuery.length < MIN_SEARCH_QUERY_LENGTH) {
      latestSearchRequestId.current += 1;
      setOrders([]);
      setHasSearched(false);
      setSearchLoading(false);
      if (mode === "manual") {
        message.warning(`Zadajte aspoň ${MIN_SEARCH_QUERY_LENGTH} znaky.`);
      }
      return;
    }

    const requestId = ++latestSearchRequestId.current;
    setSearchLoading(true);
    setHasSearched(true);
    try {
      const result = await goodsReceiptsClient.searchOrders(trimmedQuery || undefined, 1, 50);

      if (requestId !== latestSearchRequestId.current) {
        return;
      }

      setOrders(result);

      // A scanned parcel label names exactly one order: open it right away, once.
      const parcelMatches = result.filter(order => order.MatchedParcelNumber !== null);
      const [scanned] = parcelMatches;
      if (parcelMatches.length === 1 && scanned.IsSelectable && autoOpenedScan.current !== trimmedQuery) {
        autoOpenedScan.current = trimmedQuery;
        void handleSelectOrder(scanned);
      }
    } catch (error) {
      if (requestId !== latestSearchRequestId.current) {
        return;
      }

      message.error(getApiErrorMessage(error));
    } finally {
      if (requestId === latestSearchRequestId.current) {
        setSearchLoading(false);
      }
    }
  }, [handleSelectOrder]);

  React.useEffect(() => {
    if (step !== 0) {
      return;
    }

    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery) {
      return;
    }

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      void performSearch(trimmedQuery, "auto");
    }, 300);

    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
        searchDebounceRef.current = null;
      }
    };
  }, [performSearch, searchQuery, step]);

  React.useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);


  const suggestionOptions = React.useMemo<SuggestOption[]>(
    () =>
      orders.slice(0, 8).map(order => ({
        value: order.OrderCode,
        orderCode: order.OrderCode,
        label: (
          <Space style={{ width: "100%", justifyContent: "space-between" }}>
            <Text>#{order.OrderCode} - {order.CompanyName}</Text>
            {order.IsSelectable ? <Tag color="green">OK</Tag> : <Tag color="red">Blok</Tag>}
          </Space>
        )
      })),
    [orders]
  );

  const handleSearchQueryChange = React.useCallback((value: string) => {
    setSearchQuery(value);
    if (!value.trim()) {
      autoOpenedScan.current = null;
      latestSearchRequestId.current += 1;
      setOrders([]);
      setHasSearched(false);
      setSearchLoading(false);
    }
  }, []);

  const handleSuggestSelect = React.useCallback(
    (_value: string, option: SuggestOption) => {
      const order = orders.find(x => x.OrderCode === option.orderCode);
      if (order) {
        void handleSelectOrder(order);
      }
    },
    [handleSelectOrder, orders]
  );

  const updateLineQuantity = React.useCallback(
    (key: string, field: "quantityAccepted" | "quantityQuarantine" | "quantityDamaged", value?: number | null) => {
      setLineStates(prev =>
        prev.map(line => {
          if (line.key !== key) return line;

          const next = { ...line, [field]: Math.max(0, value ?? 0) };
          const total = next.quantityAccepted + next.quantityQuarantine + next.quantityDamaged;

          if (total > next.remainingQuantity + 0.000001) {
            const otherFields = ["quantityAccepted", "quantityQuarantine", "quantityDamaged"].filter(
              f => f !== field
            ) as ("quantityAccepted" | "quantityQuarantine" | "quantityDamaged")[];
            const otherSum = otherFields.reduce((sum, f) => sum + next[f], 0);
            next[field] = Math.max(0, next.remainingQuantity - otherSum);
          }

          return next;
        })
      );
    },
    []
  );

  const totalByLine = React.useCallback(
    (line: LineState) => line.quantityAccepted + line.quantityQuarantine + line.quantityDamaged,
    []
  );

  const totalOk = React.useMemo(
    () => lineStates.reduce((sum, line) => sum + line.quantityAccepted, 0),
    [lineStates]
  );
  const totalQuarantine = React.useMemo(
    () => lineStates.reduce((sum, line) => sum + line.quantityQuarantine, 0),
    [lineStates]
  );
  const totalDamaged = React.useMemo(
    () => lineStates.reduce((sum, line) => sum + line.quantityDamaged, 0),
    [lineStates]
  );
  const hasAvailableBatchNumbers = lineStates.length > 0;
  const hasQuantities = React.useMemo(
    () => lineStates.some(line => totalByLine(line) > 0),
    [lineStates, totalByLine]
  );

  const handleUpload = React.useCallback(async (options: RcCustomRequestOptions) => {
    const file = options.file as RcFile;

    try {
      const uploaded = await goodsReceiptsClient.uploadAttachment({
        data: file,
        fileName: file.name
      });

      const attachment: GoodsReceiptAttachmentRefDto = {
        Key: uploaded.Key,
        FileName: uploaded.FileName,
        FileSize: uploaded.FileSize,
        ContentType: uploaded.ContentType
      };

      setAttachments(prev => [...prev, attachment]);
      options.onSuccess?.({});
    } catch (error) {
      const err = new Error(getApiErrorMessage(error));
      options.onError?.(err);
      message.error(err.message);
    }
  }, []);

  const handleRemoveAttachment = React.useCallback((file: UploadFile) => {
    setAttachments(prev => prev.filter(x => x.FileName !== file.name));
    return true;
  }, []);

  const validateBeforeSubmit = React.useCallback((): string | null => {
    if (!selectedOrder) return "Nie je vybraná objednávka.";
    if (!intakeContext) return "Chýba kontext príjmu.";

    const linesWithQty = lineStates.filter(line => totalByLine(line) > 0);
    if (linesWithQty.length === 0) {
      return "Príjem musí obsahovať aspoň jednu položku s množstvom.";
    }

    for (const line of linesWithQty) {
      const total = totalByLine(line);
      if (total > line.remainingQuantity + 0.000001) {
        return `Položka ${line.productName} (${line.skuCode}) prekračuje dostupné množstvo.`;
      }
    }

    return null;
  }, [intakeContext, lineStates, selectedOrder, totalByLine]);

  const handleSubmit = React.useCallback(async () => {
    const validationError = validateBeforeSubmit();
    if (validationError) {
      message.error(validationError);
      return;
    }

    if (!selectedOrder) return;

    const idempotencyKey = submitIdempotencyKey ?? generateSubmitIdempotencyKey(selectedOrder.OrderCode);
    setSubmitIdempotencyKey(idempotencyKey);

    const lines: GoodsReceiptSubmitLineDto[] = lineStates
      .filter(line => totalByLine(line) > 0)
      .map(line => ({
        ReceivedOrderProductId: line.receivedOrderProductId,
        SkuId: line.skuId,
        BatchNumber: line.batchNumber,
        QuantityAccepted: line.quantityAccepted,
        QuantityQuarantine: line.quantityQuarantine,
        QuantityDamaged: line.quantityDamaged,
        BatchExpirationDate: line.batchExpirationDate
      }));

    const payload: GoodsReceiptSubmitRequestDto = {
      OrderCode: selectedOrder.OrderCode,
      ClaimId: selectedClaimId,
      ReasonText: reasonText.trim() ? reasonText.trim() : null,
      Note: noteText.trim() ? noteText.trim() : null,
      Lines: lines,
      Attachments: attachments
    };

    setSubmitting(true);
    setSubmitError(null);
    setSubmitFinalizeInconsistency(false);

    try {
      const result = await goodsReceiptsClient.submit(payload, idempotencyKey);
      setSubmitResult(result);
      setStep(2);
      message.success("Príjem bol úspešne zaevidovaný.");
    } catch (error) {
      const msg = getApiErrorMessage(error);
      let isFinalizeInconsistency = false;
      if (ApiException.isApiException(error) && error.response) {
        try {
          const responseBody = JSON.parse(error.response) as { isFinalizeInconsistency?: boolean };
          isFinalizeInconsistency = responseBody.isFinalizeInconsistency === true;
        } catch {
          // Keep default when response body is not JSON.
        }
      }

      setSubmitError(msg);
      setSubmitFinalizeInconsistency(isFinalizeInconsistency);
      setStep(2);
    } finally {
      setSubmitting(false);
    }
  }, [
    attachments,
    lineStates,
    noteText,
    reasonText,
    selectedClaimId,
    selectedOrder,
    submitIdempotencyKey,
    totalByLine,
    validateBeforeSubmit
  ]);

  const handlePrintLabels = React.useCallback(async () => {
    const linesWithQty = lineStates.filter(line => totalByLine(line) > 0);
    if (linesWithQty.length === 0) {
      message.warning("Najprv zadajte množstvá na tlač štítkov.");
      return;
    }

    setPrinting(true);
    try {
      await Promise.all(
        linesWithQty.map(line =>
          printClient.etiquette(
            line.productName,
            line.skuCode,
            line.batchNumber || undefined,
            totalByLine(line).toString(),
            "ks",
            parseInt(selectedPrinter, 10)
          )
        )
      );
      message.success("Štítky boli odoslané na tlač.");
    } catch (error) {
      message.error(`Tlač štítkov zlyhala: ${getApiErrorMessage(error)}`);
    } finally {
      setPrinting(false);
    }
  }, [lineStates, selectedPrinter, totalByLine]);

  const resetAll = React.useCallback(() => {
    setStep(0);
    setSearchQuery("");
    setHasSearched(false);
    setSearchLoading(false);
    latestSearchRequestId.current += 1;
    latestOrderContextRequestId.current += 1;
    setOrders([]);
    setSelectedOrder(null);
    setClaims([]);
    setSelectedClaimId(null);
    setIntakeContext(null);
    setSubmitIdempotencyKey(null);
    setLineStates([]);
    setReasonText("");
    setNoteText("");
    setAttachments([]);
    setUploadList([]);
    setSubmitResult(null);
    setSubmitError(null);
    setSubmitFinalizeInconsistency(false);
    setReturnedParcelChoice("restock");
    setResendResult(null);
    autoOpenedScan.current = null;
  }, []);

  // The handheld's scanner types a label as keystrokes ending with Enter. Outside a text field the
  // app's scanner hook catches them, translated by the device's character map like on the other
  // screens; a scan searches right away. An intake in progress keeps what was entered.
  const handleScan = React.useCallback(
    (code: string) => {
      if (!active) {
        return;
      }
      // An intake in progress, or a receipt that failed and may still be retried with the same
      // idempotency key, keeps what was entered.
      if (step === 1 || (step === 2 && !submitResult && !resendResult)) {
        message.info("Najprv dokončite tento príjem alebo sa vráťte na výber objednávky.");
        return;
      }
      if (step === 2) {
        resetAll();
      }
      // A new scan is a new intent, even of a label that opened its order before.
      autoOpenedScan.current = null;
      setSearchQuery(code);
      void performSearch(code, "manual");
    },
    [active, performSearch, resendResult, resetAll, step, submitResult]
  );
  useKeyboardScanner(handleScan);

  const handleBackToEditAfterFailure = React.useCallback(() => {
    // Payload may change after returning to edit; force a new idempotency key.
    setSubmitIdempotencyKey(null);
    setStep(1);
  }, []);

  const orderColumns: ColumnsType<IncomingOrderSearchResultDto> = [
    {
      title: "Objednávka",
      dataIndex: "OrderCode",
      key: "orderCode",
      render: (_, order) => <b>#{order.OrderCode}</b>
    },
    {
      title: "Firma / zákazník",
      key: "customer",
      render: (_, order) => (
        <Space direction="vertical" size={0}>
          <Text>{order.CompanyName}</Text>
          {order.UserName ? <Text type="secondary">{order.UserName}</Text> : null}
        </Space>
      )
    },
    {
      title: "Kontakt",
      key: "contact",
      render: (_, order) => (
        <Space direction="vertical" size={0}>
          {order.Email ? <Text>{order.Email}</Text> : null}
          {order.Phone ? <Text type="secondary">{order.Phone}</Text> : null}
        </Space>
      )
    },
    {
      title: "Stav",
      key: "status",
      render: (_, order) =>
        order.IsSelectable ? (
          <Tag color="green">Odoslaná</Tag>
        ) : (
          <Tag color="red">{order.BlockingReason || "Nevyberateľná"}</Tag>
        )
    },
    {
      title: "",
      key: "action",
      render: (_, order) => (
        <Button
          type="primary"
          onClick={() => void handleSelectOrder(order)}
          disabled={!order.IsSelectable}
        >
          Vybrať
        </Button>
      )
    }
  ];

  const groupedLines = React.useMemo(() => {
    const map = new Map<string, LineState[]>();
    lineStates.forEach(line => {
      const key = `${line.productName}`;
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(line);
    });
    return Array.from(map.entries());
  }, [lineStates]);

  return (
    <div>
      <Steps
        current={step}
        style={{ marginBottom: 24 }}
        items={[
          { title: "Výber objednávky" },
          {
            title:
              returnedParcelChoice === "decide"
                ? "Rozhodnutie"
                : returnedParcelChoice === "resend"
                  ? "Nový štítok"
                  : "Klasifikácia príjmu"
          },
          { title: "Výsledok" }
        ]}
      />

      {step === 0 && (
          <Card>
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <Space direction="vertical" size="small" style={{ width: "100%" }}>
                <AutoComplete
                  style={{ width: "100%" }}
                  options={suggestionOptions}
                  value={searchQuery}
                  onChange={handleSearchQueryChange}
                  onSelect={(value, option) => handleSuggestSelect(value, option as SuggestOption)}
                  filterOption={false}
                >
                  <Input
                    placeholder="Číslo objednávky alebo balíka, firma, e-mail, telefón... (autosuggest od 2 znakov)"
                    allowClear
                    onPressEnter={() => void performSearch(searchQuery, "manual")}
                  />
                </AutoComplete>
                <Space wrap>
                  <Button type="primary" loading={searchLoading} onClick={() => void performSearch(searchQuery, "manual")}>
                    Hľadať
                  </Button>
                  <Button
                    onClick={() => {
                      setSearchQuery("");
                      void performSearch("", "manual");
                    }}
                  >
                    Posledné objednávky
                  </Button>
                </Space>
              </Space>
              <Text type="secondary">
                Štítok vráteného balíka stačí naskenovať, objednávka sa otvorí sama.
              </Text>
              <Text type="secondary">
                Vyhľadávanie podľa textu spustíte od {MIN_SEARCH_QUERY_LENGTH} znakov.
              </Text>
              <Text type="secondary">
                Zobrazuje sa maximálne 50 výsledkov na jedno vyhľadanie.
              </Text>

            <Table
              rowKey={row => row.OrderCode}
              loading={searchLoading}
              dataSource={orders}
              columns={orderColumns}
              pagination={{ pageSize: 10, hideOnSinglePage: true }}
              locale={{
                emptyText: hasSearched
                  ? "Nenašli sa žiadne objednávky."
                  : "Zadajte výraz a kliknite na Hľadať, alebo načítajte posledné objednávky."
              }}
            />
          </Space>
        </Card>
      )}

      {step === 1 && returnedParcelChoice === "decide" && selectedOrder && intakeContext?.ReturnedShipment && (
        <ReturnedParcelDecision
          orderCode={selectedOrder.OrderCode}
          companyName={selectedOrder.CompanyName}
          returned={intakeContext.ReturnedShipment}
          onResend={() => setReturnedParcelChoice("resend")}
          onRestock={() => setReturnedParcelChoice("restock")}
          onBack={() => {
            setReturnedParcelChoice("restock");
            setStep(0);
          }}
        />
      )}

      {step === 1 && returnedParcelChoice === "resend" && selectedOrder && intakeContext?.ReturnedShipment && (
        <ReturnedParcelLabelForm
          orderCode={selectedOrder.OrderCode}
          companyName={selectedOrder.CompanyName}
          returned={intakeContext.ReturnedShipment}
          onBooked={result => {
            setResendResult(result);
            setStep(2);
          }}
          onBack={() => setReturnedParcelChoice("decide")}
        />
      )}

      {step === 1 && returnedParcelChoice === "restock" && (
        <Spin spinning={contextLoading || submitting}>
          <Space direction="vertical" size="large" style={{ width: "100%" }}>
            <Card>
              <Descriptions column={1} size="small" title="Vybraná objednávka">
                <Descriptions.Item label="Objednávka">
                  #{selectedOrder?.OrderCode}
                </Descriptions.Item>
                <Descriptions.Item label="Firma / zákazník">
                  {selectedOrder?.CompanyName}
                </Descriptions.Item>
              </Descriptions>

              <Divider />

              <Space direction="vertical" style={{ width: "100%" }}>
                <Text strong>Reklamácia</Text>
                <Select
                  style={{ width: "100%" }}
                  value={selectedClaimId === null ? "none" : String(selectedClaimId)}
                  onChange={value => setSelectedClaimId(value === "none" ? null : Number(value))}
                >
                  <Select.Option value="none">Bez reklamácie</Select.Option>
                  {claims.map(claim => (
                    <Select.Option key={claim.ClaimId} value={String(claim.ClaimId)}>
                      {claim.DisplayName}
                    </Select.Option>
                  ))}
                </Select>
              </Space>
            </Card>

            <Card title="Položky a šarže">
              <Space direction="vertical" size="large" style={{ width: "100%" }}>
                {hasAvailableBatchNumbers ? (
                  groupedLines.map(([groupName, lines]) => (
                    <div key={groupName}>
                      <Text strong>{groupName}</Text>
                      <Table
                        rowKey={row => row.key}
                        dataSource={lines}
                        pagination={false}
                        size="small"
                        columns={[
                          {
                            title: "SKU",
                            key: "sku",
                            render: (_, row) => (
                              <Space direction="vertical" size={0}>
                                <Text>{row.skuCode}</Text>
                                <Text type="secondary">
                                  Šarža: {row.batchNumber || "Bez šarže"}
                                </Text>
                              </Space>
                            )
                          },
                          {
                            title: "Zostáva",
                            key: "remaining",
                            render: (_, row) => <Text>{row.remainingQuantity}</Text>
                          },
                          {
                              title: "OK",
                              key: "ok",
                              render: (_, row) => (
                                <InputNumber
                                  min={0}
                                  value={row.quantityAccepted}
                                  onChange={value => updateLineQuantity(row.key, "quantityAccepted", value)}
                                  style={{ width: 90 }}
                                />
                              )
                          },
                          {
                            title: "Na kontrolu",
                            key: "toCheck",
                            render: (_, row) => (
                              <InputNumber
                                min={0}
                                value={row.quantityQuarantine}
                                onChange={value => updateLineQuantity(row.key, "quantityQuarantine", value)}
                                style={{ width: 110 }}
                              />
                            )
                          },
                          {
                            title: "Poškodené",
                            key: "damaged",
                            render: (_, row) => (
                              <InputNumber
                                min={0}
                                value={row.quantityDamaged}
                                onChange={value => updateLineQuantity(row.key, "quantityDamaged", value)}
                                style={{ width: 110 }}
                              />
                            )
                          },
                          {
                            title: "Prijaté",
                            key: "received",
                            render: (_, row) => <Text>{totalByLine(row)}</Text>
                          }
                        ]}
                      />
                    </div>
                  ))
                ) : (
                    <Alert
                      type="warning"
                      showIcon
                      message="Pre túto objednávku momentálne nie sú k dispozícii položky k príjmu."
                      description="Prejdite späť a vyberte inú objednávku s dostupnými položkami."
                    />
                )}
              </Space>
            </Card>

            <Card title="Poznámka a prílohy">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Input.TextArea
                  rows={3}
                  placeholder="Dôvod vrátenia (voliteľné)"
                  value={reasonText}
                  onChange={e => setReasonText(e.target.value)}
                />
                <Mentions
                  rows={3}
                  placeholder="Interná poznámka (voliteľné) — napíšte @ pre označenie kolegu"
                  value={noteText}
                  onChange={setNoteText}
                >
                  {mentionableAdmins.map(admin => (
                    <Mentions.Option key={admin.DisplayName} value={admin.DisplayName}>
                      {admin.DisplayName}
                    </Mentions.Option>
                  ))}
                </Mentions>
                <Upload
                  multiple
                  customRequest={options => {
                    void handleUpload(options as unknown as RcCustomRequestOptions);
                  }}
                  onRemove={handleRemoveAttachment}
                  fileList={uploadList}
                  onChange={info => setUploadList(info.fileList)}
                >
                  <Button icon={<UploadOutlined />}>Nahrať prílohy</Button>
                </Upload>
                <Text type="secondary">Maximálna veľkosť jedného súboru je 100 MB.</Text>
              </Space>
            </Card>

            <Card>
              <Space direction="vertical" style={{ width: "100%" }}>
                <Text strong>Súčet</Text>
                <Space wrap>
                <Tag color="green">Prijaté: {totalOk}</Tag>
                <Tag color="orange">Na kontrolu: {totalQuarantine}</Tag>
                <Tag color="red">Poškodené: {totalDamaged}</Tag>
                </Space>
                <Divider style={{ margin: "8px 0" }} />
                <Space wrap align="center">
                  <Text strong>Tlačiareň:</Text>
                  <Radio.Group
                    optionType="button"
                    buttonStyle="solid"
                    options={printerOptions}
                    value={selectedPrinter}
                    onChange={e => setSelectedPrinter(e.target.value as PrinterPort)}
                  />
                  <Button
                    icon={<PrinterOutlined />}
                    disabled={!hasAvailableBatchNumbers}
                    loading={printing}
                    onClick={() => void handlePrintLabels()}
                  >
                    Vytlačiť štítky
                  </Button>
                </Space>
                <Space>
                  <Button onClick={() => setStep(0)}>Späť</Button>
                  <Button
                    type="primary"
                    onClick={() => void handleSubmit()}
                    loading={submitting}
                    disabled={!hasQuantities}
                  >
                    Potvrdiť príjem
                  </Button>
                </Space>
              </Space>
            </Card>
          </Space>
        </Spin>
      )}

      {step === 2 && resendResult && selectedOrder && (
        <ReturnedParcelResult orderCode={selectedOrder.OrderCode} result={resendResult} onNew={resetAll} />
      )}

      {step === 2 && !resendResult && (
        <>
          {submitResult ? (
            <Result
              status="success"
              icon={<CheckCircleOutlined />}
              title="Príjem bol úspešne zaevidovaný"
              subTitle={`Pohoda príjemka: ${submitResult.PohodaReceiptNumber}`}
              extra={[
                <Tag key="ok" color="green">
                  Prijaté: {submitResult.TotalAccepted}
                </Tag>,
                <Tag key="check" color="orange">
                  Na kontrolu: {submitResult.TotalQuarantine}
                </Tag>,
                <Tag key="damaged" color="red">
                  Poškodené: {submitResult.TotalDamaged}
                </Tag>,
                <Text key="case-id">Príjemka: {formatGoodsReceiptNumber(submitResult.GoodsReceiptId)}</Text>,
                <Button key="new" type="primary" onClick={resetAll}>
                  Nový príjem
                </Button>
              ]}
            />
          ) : (
            <Result
              status="error"
              title="Príjem sa nepodarilo zaevidovať"
              subTitle={submitError || "Skúste to znovu."}
              extra={[
                !submitFinalizeInconsistency ? (
                  <Button key="back" onClick={handleBackToEditAfterFailure}>
                    Späť na úpravu
                  </Button>
                ) : null,
                <Button key="retry" type="primary" onClick={() => void handleSubmit()} loading={submitting}>
                  Skúsiť znova
                </Button>
              ]}
            >
              <Alert
                type="error"
                message={submitError || "Nastala neočakávaná chyba."}
                showIcon
              />
            </Result>
          )}
        </>
      )}
    </div>
  );
}
